import request from '#api/axios';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { IExercisePreset } from '#types/exercisePresets';
import {
  UseMutationOptions,
  useMutation,
  useQuery
} from '@tanstack/react-query';

export const getExercisePresets = async () => {
  const res = await request<IExercisePreset[]>({
    method: 'get',
    url: '/exercise-presets'
  });

  return res.data;
};

export const useGetExercisePresets = () => {
  return useQuery(queryKeyFactory.GET_EXERCISE_PRESETS(), getExercisePresets);
};

export const postExercisePreset = async (data: Omit<IExercisePreset, 'id'>) => {
  const res = await request({
    method: 'post',
    url: '/exercise-presets',
    data
  });

  return res.data;
};

const getExercisePresetsDetail = async (id: string) => {
  const res = await request<IExercisePreset>({
    method: 'get',
    url: `/exercise-presets/${id}`
  });

  return res.data;
};

export const useGetExercisePresetsDetail = (id: string | undefined) => {
  return useQuery(
    [queryKeyFactory.GET_EXERCISE_PRESETS(), id],
    () => getExercisePresetsDetail(id as string),
    { enabled: !!id }
  );
};

export const usePostExercisePreset = (
  options?:
    | Omit<
        UseMutationOptions<
          unknown,
          unknown,
          Omit<IExercisePreset, 'id'>,
          unknown
        >,
        'mutationFn' | 'mutationKey'
      >
    | undefined
) => {
  return useMutation(
    queryKeyFactory.POST_EXERCISE_PRESET(),
    postExercisePreset,
    options
  );
};

const patchExercisePreset = async ({
  id,
  params
}: {
  id: string;
  params: IExercisePreset;
}) => {
  const res = await request({
    method: 'patch',
    url: `/exercise-presets/${id}`,
    data: params
  });

  return res.data;
};

export const usePatchExercisePreset = ({
  onSuccess,
  onError
}: {
  onSuccess: () => void;
  onError: () => void;
}) => {
  return useMutation(patchExercisePreset, {
    onSuccess,
    onError
  });
};

const deleteExercisePreset = async (id: string) => {
  const res = await request({
    method: 'delete',
    url: `/exercise-presets/${id}`
  });

  return res.data;
};
export const useDeleteExercisePreset = (
  options?: UseMutationOptions<unknown, unknown, string, unknown>
) => {
  return useMutation(deleteExercisePreset, options);
};
